import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { apiFetch } from '@/lib/apiClient';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { emailApiPath } from '@/lib/email-api';
import { deleteIntegration, setPrimaryIntegrationForModule } from '@/lib/api/endpoints/integrations';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useMediaQuery } from '@/common/hooks/useMediaQuery';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useHideDemo } from '@/features/demo/demoVisibility';
import { isDemoIntegrationId } from './demoAccounts';
import { getProviderDisplay, isV2, type EmailProvider } from './provider-display';
import {
  IntegrationSyncStatusBadge,
  type IntegrationSyncErrorCode,
} from '@/common/components/IntegrationSyncStatusBadge';

interface EmailAccount {
  id: string;
  email: string;
  provider: EmailProvider;
  tokenValid: boolean;
  isPrimary?: boolean;
  /** 'disabled' = Retain-disconnected (data kept, not syncing). */
  status?: 'active' | 'disabled';
  syncError?: string | null;
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncErrorAt?: string | null;
}

interface ManageAccountsModalProps {
  onClose: () => void;
  moduleId?: string;
}

export function ManageAccountsModal({ onClose, moduleId }: ManageAccountsModalProps) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  const [hideDemo] = useHideDemo();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starHoverLocked, setStarHoverLocked] = useState(false);
  const [disconnectConfirmation, setDisconnectConfirmation] = useState<{ accountId: string; email: string; provider: EmailProvider } | null>(null);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEscapeKey(onClose);
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose);

  const fetchAccounts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const scopeQuery = moduleId ? `?moduleId=${encodeURIComponent(moduleId)}` : '';
      const data = await apiFetch<{ accounts: EmailAccount[] }>(
        `${emailApiPath()}/status${scopeQuery}`,
        { authToken: token },
      );
      setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Failed to fetch email accounts:', err);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [token, moduleId]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleDisconnect = (account: EmailAccount) => {
    setDisconnectConfirmation({ accountId: account.id, email: account.email, provider: account.provider });
  };

  const confirmDisconnect = async (mode: 'remove' | 'retain') => {
    if (!disconnectConfirmation || !token) return;
    const { accountId } = disconnectConfirmation;
    setDisconnecting(true);
    setError(null);
    setDisconnectConfirmation(null);
    try {
      await deleteIntegration(token, accountId, mode);
      window.dispatchEvent(new CustomEvent('integrationAccountDisconnected', { detail: { accountId } }));
      queryClient.invalidateQueries({ queryKey: ['email-status-module'] });
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] });
      await fetchAccounts();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError('Failed to disconnect account.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSetPrimary = async (accountId: string) => {
    if (!token || !moduleId) return;
    // Clear any lingering hover styles (star hover hides disconnect via inline style)
    document.querySelectorAll('[data-disconnect-btn]').forEach((el) => {
      (el as HTMLElement).style.opacity = '';
      (el as HTMLElement).style.pointerEvents = '';
    });
    document.querySelectorAll('[data-email-wrap]').forEach((el) => {
      (el as HTMLElement).style.marginLeft = '';
    });

    try {
      await setPrimaryIntegrationForModule(token, accountId, moduleId);

      // Lock star hover for 800ms so the swap animation plays without
      // the other account's star immediately triggering on mouse position
      setStarHoverLocked(true);
      setTimeout(() => setStarHoverLocked(false), 800);

      // FLIP animation: capture current positions before reorder
      const rows = document.querySelectorAll('[data-account-row]');
      const firstPositions = new Map<string, DOMRect>();
      rows.forEach((row) => {
        const id = row.getAttribute('data-account-id');
        if (id) firstPositions.set(id, row.getBoundingClientRect());
      });

      // Refetch to sync with server, keep primary-first order
      const scopeQuery = `?moduleId=${encodeURIComponent(moduleId)}`;
      const data = await apiFetch<{ accounts: EmailAccount[] }>(
        `${emailApiPath()}/status${scopeQuery}`,
        { authToken: token },
      );
      const sorted = [...(data.accounts || [])].sort((a, b) => {
        if (a.isPrimary && !b.isPrimary) return -1;
        if (!a.isPrimary && b.isPrimary) return 1;
        return 0;
      });
      setAccounts(sorted);

      if (prefersReducedMotion) return;
      // After React renders, animate from old positions to new
      requestAnimationFrame(() => {
        const newRows = document.querySelectorAll('[data-account-row]');
        newRows.forEach((row) => {
          const id = row.getAttribute('data-account-id');
          if (!id) return;
          const first = firstPositions.get(id);
          if (!first) return;
          const last = row.getBoundingClientRect();
          const deltaY = first.top - last.top;
          if (Math.abs(deltaY) < 1) return;
          const el = row as HTMLElement;
          el.style.transform = `translateY(${deltaY}px)`;
          el.style.transition = 'none';
          requestAnimationFrame(() => {
            el.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
            el.style.transform = 'translateY(0)';
            const cleanup = () => { el.style.transform = ''; el.style.transition = ''; el.removeEventListener('transitionend', cleanup); };
            el.addEventListener('transitionend', cleanup);
          });
        });
      });
    } catch (err) {
      console.error('Failed to set primary account:', err);
      setError('Failed to set primary account.');
    }
  };

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    if (!token) return;
    if (provider === 'google') {
      window.dispatchEvent(new Event('triggerGoogleOAuth'));
      onClose();
      return;
    }
    // Microsoft: same current-window redirect as Google (not a popup), via the
    // one connect helper. No integrationId → FLOW A (enroll a new account): the
    // backend keeps prompt=select_account so the account picker shows. The
    // provider-oauth callback redirects back here with ?integration_connected=true;
    // EmailPanel reads `email_oauth_in_progress` and refreshes the list on return.
    try { sessionStorage.setItem('email_oauth_in_progress', 'true'); } catch { /* best-effort */ }
    try {
      await connectProvider({ provider: 'microsoft', authToken: token });
    } catch {
      try { sessionStorage.removeItem('email_oauth_in_progress'); } catch { /* best-effort */ }
      setError('Failed to connect Microsoft.');
    }
  };

  const connectOptions = isV2
    ? [
        { provider: 'google' as const, label: 'Connect Google' },
        { provider: 'microsoft' as const, label: 'Connect Microsoft' },
      ]
    : [{ provider: 'google' as const, label: 'Add Account' }];

  // The seeded demo (Boris) account isn't a real integration and can't be
  // disconnected, so hide it from the list when demo data is hidden.
  const visibleAccounts = hideDemo
    ? accounts.filter((a) => !isDemoIntegrationId(a.id))
    : accounts;

  // Primary (write) account selection — only meaningful with 2+ accounts and
  // a module to scope it to (set-primary is a per-module designation).
  const showPrimary = visibleAccounts.length >= 2 && Boolean(moduleId);
  // If none is marked primary, treat the first account as the default primary
  const hasPrimary = visibleAccounts.some(a => a.isPrimary);

  // Hover/focus affordances share one body so keyboard focus gets the same
  // reveal the mouse gets (the inline-style equivalent of :hover/:focus).
  const revealDisconnectLabel = (btn: HTMLElement) => {
    if (disconnecting) return;
    // If a star hover hid this button (opacity 0), focusing it must un-hide
    // it — otherwise Tab can land on an invisible control.
    btn.style.opacity = '';
    btn.style.pointerEvents = '';
    btn.style.color = '#dc2626';
    const label = btn.querySelector('span') as HTMLElement | null;
    if (label) { label.style.maxWidth = '80px'; label.style.marginLeft = '4px'; }
  };
  const collapseDisconnectLabel = (btn: HTMLElement) => {
    btn.style.color = 'var(--color-text-muted)';
    const label = btn.querySelector('span') as HTMLElement | null;
    if (label) { label.style.maxWidth = '0'; label.style.marginLeft = '0'; }
  };
  const revealSetPrimary = (btn: HTMLElement, isPrimary: boolean) => {
    if (isPrimary || starHoverLocked) return;
    btn.style.color = '#f59e0b';
    const row = btn.closest('[data-account-row]');
    const emailWrap = row?.querySelector('[data-email-wrap]') as HTMLElement | null;
    if (emailWrap) { emailWrap.style.marginLeft = 'auto'; }
    const setLabel = btn.querySelector('[data-set-primary-label]') as HTMLElement | null;
    if (setLabel) { setLabel.style.maxWidth = '140px'; setLabel.style.marginLeft = '8px'; }
    const dcBtn = row?.querySelector('[data-disconnect-btn]') as HTMLElement | null;
    if (dcBtn) { dcBtn.style.opacity = '0'; dcBtn.style.pointerEvents = 'none'; }
  };
  const collapseSetPrimary = (btn: HTMLElement, isPrimary: boolean) => {
    if (isPrimary) return;
    btn.style.color = 'var(--color-text-muted, #9ca3af)';
    const row = btn.closest('[data-account-row]');
    const emailWrap = row?.querySelector('[data-email-wrap]') as HTMLElement | null;
    if (emailWrap) { emailWrap.style.marginLeft = ''; }
    const setLabel = btn.querySelector('[data-set-primary-label]') as HTMLElement | null;
    if (setLabel) { setLabel.style.maxWidth = '0'; setLabel.style.marginLeft = '0'; }
    const dcBtn = row?.querySelector('[data-disconnect-btn]') as HTMLElement | null;
    if (dcBtn) { dcBtn.style.opacity = ''; dcBtn.style.pointerEvents = ''; }
  };

  return (<>
    {createPortal(
    <div
      onClick={handleOverlayClick}
      onMouseDown={handleOverlayMouseDown}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000,
      }}
    >
      <div
        ref={modalContentRef as React.RefObject<HTMLDivElement>}
        style={{
          background: 'var(--color-surface, white)',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          maxWidth: '400px', width: '100%',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--color-border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-heading)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-text-heading)' }}>
              Manage Accounts
            </h2>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px', fontSize: '24px', lineHeight: 1,
            color: 'var(--color-text-muted)',
          }}>&times;</button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error && <p style={{ fontSize: '12px', color: '#dc2626', margin: 0 }}>{error}</p>}

          {loading ? (
            <div style={{ fontSize: '14px', color: 'var(--color-text-faint)', padding: '12px 0', textAlign: 'center' }}>
              Loading accounts...
            </div>
          ) : visibleAccounts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {visibleAccounts.map((account, idx) => {
                const display = getProviderDisplay(account.provider);
                const isDisabled = account.status === 'disabled';
                // Exactly one account is primary: either explicitly flagged,
                // or the first account as default when none is flagged yet.
                const isPrimary = hasPrimary ? account.isPrimary === true : idx === 0;
                return (
                  <div
                    key={account.id}
                    data-account-row
                    data-account-id={account.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', borderRadius: '6px',
                      border: '1px solid var(--color-border)',
                      backgroundColor: isDisabled ? 'var(--color-surface-hover, #f5f5f5)' : 'var(--color-surface)',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      {showPrimary && !isDisabled && (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!isPrimary) handleSetPrimary(account.id); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: isPrimary ? 'default' : 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            transition: 'color 0.15s ease',
                            color: isPrimary ? '#f59e0b' : 'var(--color-text-muted, #9ca3af)',
                            // When PRIMARY label is above the email, push star down to align with email line
                            alignSelf: isPrimary ? 'flex-end' : 'center',
                          }}
                          onMouseEnter={(e) => revealSetPrimary(e.currentTarget, isPrimary)}
                          onMouseLeave={(e) => collapseSetPrimary(e.currentTarget, isPrimary)}
                          onFocus={(e) => revealSetPrimary(e.currentTarget, isPrimary)}
                          onBlur={(e) => collapseSetPrimary(e.currentTarget, isPrimary)}
                          title={isPrimary ? 'Primary account' : 'Set as primary'}
                          aria-label={isPrimary ? `${account.email} is the primary account` : `Set ${account.email} as primary`}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill={isPrimary ? '#f59e0b' : 'none'} stroke={isPrimary ? '#f59e0b' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                          {!isPrimary && (
                            <span
                              data-set-primary-label
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#d97706',
                                maxWidth: 0,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                transition: 'max-width 0.2s ease, margin-left 0.2s ease',
                                marginLeft: 0,
                              }}>
                              Set as primary
                            </span>
                          )}
                        </button>
                      )}
                      <img src={display.logo} width="18" height="18" alt={display.name} style={{ flexShrink: 0, opacity: isDisabled ? 0.5 : 1 }} />
                      <div data-email-wrap style={{ flex: 1, minWidth: 0, transition: 'margin-left 0.2s ease' }}>
                        {showPrimary && !isDisabled && (
                          <div style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: 'var(--color-primary, #1a9ba5)',
                            letterSpacing: '0.04em',
                            maxHeight: isPrimary ? '18px' : '0px',
                            marginBottom: isPrimary ? '2px' : '0px',
                            overflow: 'hidden',
                            opacity: isPrimary ? 1 : 0,
                            transition: 'max-height 0.3s ease, margin-bottom 0.3s ease, opacity 0.3s ease',
                          }}>
                            PRIMARY
                          </div>
                        )}
                        <span style={{
                          fontSize: '14px', color: isDisabled ? 'var(--color-text-muted)' : 'var(--color-text)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                        }} title={account.email}>
                          {account.email}
                        </span>
                        {isDisabled && (
                          <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', display: 'block', marginTop: 2 }}>
                            Disconnected — data retained
                          </span>
                        )}
                        {account.syncErrorCode && (
                          <div style={{ marginTop: 2 }}>
                            <IntegrationSyncStatusBadge
                              syncErrorCode={account.syncErrorCode}
                              syncError={account.syncError}
                              syncErrorAt={account.syncErrorAt}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {isDisabled ? (
                      <button
                        onClick={() => handleConnect(account.provider as 'google' | 'microsoft')}
                        style={{
                          background: 'none', border: '1px solid var(--color-border)',
                          borderRadius: '6px', cursor: 'pointer', padding: '4px 10px',
                          fontSize: '12px', fontWeight: 600, color: 'var(--color-primary, #1a9ba5)',
                          flexShrink: 0, whiteSpace: 'nowrap',
                          alignSelf: isPrimary ? 'flex-end' : 'center',
                        }}
                        title="Reconnect this account (resumes syncing; retained data stays)"
                      >
                        Reconnect
                      </button>
                    ) : (
                      <button
                        data-disconnect-btn
                        onClick={() => handleDisconnect(account)}
                        disabled={disconnecting}
                        style={{
                          background: 'none', border: 'none',
                          cursor: disconnecting ? 'not-allowed' : 'pointer',
                          padding: '4px', color: 'var(--color-text-muted)',
                          opacity: disconnecting ? 0.5 : 1,
                          transition: 'color 0.15s ease, opacity 0.2s ease',
                          display: 'flex', alignItems: 'center', flexShrink: 0,
                          alignSelf: isPrimary ? 'flex-end' : 'center',
                        }}
                        onMouseEnter={(e) => revealDisconnectLabel(e.currentTarget)}
                        onMouseLeave={(e) => collapseDisconnectLabel(e.currentTarget)}
                        onFocus={(e) => revealDisconnectLabel(e.currentTarget)}
                        onBlur={(e) => collapseDisconnectLabel(e.currentTarget)}
                        title="Disconnect"
                        aria-label={`Disconnect ${account.email}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                        <span style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          maxWidth: 0,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          transition: 'max-width 0.2s ease, margin-left 0.2s ease',
                          marginLeft: 0,
                        }}>
                          Disconnect
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '12px 0' }}>
              No email accounts connected
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {connectOptions.map(({ provider, label }) => {
              const display = getProviderDisplay(provider);
              return (
                <button
                  key={provider}
                  onClick={() => handleConnect(provider)}
                  style={{
                    width: '100%', backgroundColor: 'var(--color-surface)',
                    padding: '10px 12px', border: '1px solid var(--color-border)',
                    borderRadius: '6px', fontSize: '13px', fontWeight: 500,
                    color: 'var(--color-text-subtle)', cursor: 'pointer',
                    transition: 'font-weight 0.1s ease, background 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.fontWeight = '600';
                    e.currentTarget.style.background = 'var(--color-surface-hover, #f5f5f5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.fontWeight = '500';
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <img src={display.logo} width="16" height="16" alt="" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
    )}

    {disconnectConfirmation && createPortal(
      <div
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10001,
        }}
        onClick={() => setDisconnectConfirmation(null)}
      >
        <div
          style={{
            background: 'var(--color-surface, white)', borderRadius: '12px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px', width: '100%', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '24px 24px 8px' }}>
            <p style={{ fontSize: '16px', color: 'var(--color-text)', lineHeight: 1.6, margin: 0 }}>
              Disconnect <strong>{disconnectConfirmation.email}</strong>?
            </p>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.6, margin: '8px 0 0' }}>
              This applies to the whole account across every module. Choose what
              happens to data already synced from it.
            </p>
          </div>
          {/* Remove (default) vs Retain. Remove is emphasized per the locked spec. */}
          <div style={{ padding: '8px 24px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={() => confirmDisconnect('remove')}
              style={{
                padding: '12px 14px', background: '#dc2626', color: 'white',
                border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
                fontWeight: 600, textAlign: 'left', lineHeight: 1.4,
              }}
            >
              Remove all data
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 400, opacity: 0.9, marginTop: 2 }}>
                Delete everything synced from this account.
              </span>
            </button>
            <button
              onClick={() => confirmDisconnect('retain')}
              style={{
                padding: '12px 14px', background: 'white', color: '#374151',
                border: '1px solid var(--color-border-strong, #d1d5db)', borderRadius: '10px', fontSize: '14px', cursor: 'pointer',
                fontWeight: 600, textAlign: 'left', lineHeight: 1.4,
              }}
            >
              Keep data, stop syncing
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 400, color: 'var(--color-text-muted)', marginTop: 2 }}>
                Keep synced items as read-only; you can reconnect later.
              </span>
            </button>
            <button
              onClick={() => setDisconnectConfirmation(null)}
              style={{
                padding: '8px 16px', background: 'none', color: 'var(--color-text-muted)',
                border: '1px solid var(--color-border-strong, #d1d5db)', borderRadius: '12px', fontSize: '14px', cursor: 'pointer',
              }}
            >Cancel</button>
          </div>
        </div>
      </div>,
      document.body,
    )}
  </>);
}
